'use client';
import React, { useEffect, useState } from 'react';
import { SocialLink, getCachedSocialLinks, socialLinksService } from '@/lib/social/socialLinksService';

interface IconProps {
  size?: number;
}

function InstagramLogo({ size = 20 }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
    </svg>
  );
}

function YoutubeLogo({ size = 20 }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17" />
      <path d="m10 15 5-3-5-3z" />
    </svg>
  );
}

function FacebookLogo({ size = 20 }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
    </svg>
  );
}

function LinkedinLogo({ size = 20 }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
      <rect x="2" y="9" width="4" height="12" />
      <circle cx="4" cy="4" r="2" />
    </svg>
  );
}

const SUPPORTED_PLATFORMS = ['instagram', 'facebook', 'youtube', 'linkedin'];

function PlatformIcon({ platform, size }: { platform: string; size: number }) {
  if (platform === 'instagram') return <InstagramLogo size={size} />;
  if (platform === 'facebook') return <FacebookLogo size={size} />;
  if (platform === 'youtube') return <YoutubeLogo size={size} />;
  if (platform === 'linkedin') return <LinkedinLogo size={size} />;
  return null;
}

function normaliseLinks(rows: SocialLink[]): SocialLink[] {
  return rows
    .filter((r) => SUPPORTED_PLATFORMS.includes(r.platform.toLowerCase()))
    .map((r) => ({ ...r, platform: r.platform.toLowerCase() }))
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

function useSocialLinks(): SocialLink[] {
  const [links, setLinks] = useState<SocialLink[]>(() => {
    const cached = getCachedSocialLinks();
    return cached ? normaliseLinks(cached) : [];
  });
  useEffect(() => {
    if (getCachedSocialLinks()) return;
    let cancelled = false;
    socialLinksService
      .getLinks()
      .then((rows) => {
        if (!cancelled) setLinks(normaliseLinks(rows));
      })
      .catch(() => {
        if (!cancelled) setLinks([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return links;
}

interface SocialIconLinksProps {
  variant: 'footer' | 'header';
}

export function SocialIconLinks({ variant }: SocialIconLinksProps) {
  const links = useSocialLinks();
  if (links.length === 0) return null;

  const footer = variant === 'footer';
  const base =
    'inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2';
  const colours = footer
    ? 'bg-[#C6A667] text-[#581C32] hover:bg-[#D4B97F] focus-visible:ring-[#F8F3E9] focus-visible:ring-offset-[#581C32]'
    : 'bg-[#581C32] text-[#C6A667] hover:bg-[#6E2842] focus-visible:ring-[#C6A667] focus-visible:ring-offset-white';

  return (
    <ul className="flex items-center gap-2.5" aria-label={footer ? 'Social media links' : undefined}>
      {links.map((l) => (
        <li key={l.platform}>
          <a
            href={l.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={l.label}
            title={l.label}
            className={`${base} ${colours}`}
          >
            <PlatformIcon platform={l.platform} size={20} />
          </a>
        </li>
      ))}
    </ul>
  );
}

export function FooterFollowUs() {
  const links = useSocialLinks();
  if (links.length === 0) return null;
  return (
    <div className="flex flex-col gap-3">
      <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-[#C6A667]">Follow us</h2>
      <SocialIconLinks variant="footer" />
    </div>
  );
}
